import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, RotateCcw, UserCheck } from 'lucide-react';

import {
	PunchupTableContainer,
	PunchupHeaderRow,
	PunchupDataRow,
	PunchupColumnHeader,
	PunchupTableEmptyRow,
} from 'features/common/components/Punchup/primitives/table/PunchupTable';
import { PunchupCombobox } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupCombobox';
import { PunchupTag } from 'features/common/components/Punchup/primitives/display/PunchupTag';
import { PunchupTicketTypeTag } from 'features/common/components/Punchup/primitives/display/PunchupTicketTypeTag';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupSpinner } from 'features/common/components/Punchup/primitives/feedback/PunchupSpinner';
import { PunchupErrorState } from 'features/common/components/Punchup/primitives/feedback/PunchupErrorState';
import { PunchupConfirmDialog } from 'features/common/components/Punchup/composites/dialogs/PunchupConfirmDialog';
import { useTicketTypeColors } from 'features/common/hooks/useTicketTypeColors';
import { ResponsesData } from 'features/CustomFieldsResponses/types';
import { airbrake } from 'ErrorBoundary';
import { canUndoCheckIn } from 'utils/utils';

import { ATTENDEE_PAGE_SIZE, getEventAttendees, undoCheckIn, validateTicketsManually } from '../services';
import { extractValidationError } from '../utils';
import { ManualCheckInState } from '../types';
import styles from './ManualApprovalPanel.module.css';

/** Scanned-tag backgrounds cloned from the Attendee List (CustomFieldsResponse `statusColors`) so the manual
 * table reads the SAME chips. PunchupTag's `color-mix()` needs a concrete hex (a token var won't resolve), and
 * green features stay self-contained for the downstroke lift, so the two literals live here too. */
const SCANNED_BG = '#7CC11F'; // = --punchup-color-status-success (lime) — Scanned
const NOT_SCANNED_BG = '#787B7E'; // grey — Not scanned

interface ManualApprovalPanelProps {
	eventId: string;
	/** Lifts the manual check-in verdict to the page's right-column ManualCheckInResult panel. */
	onResult: (result: ManualCheckInState) => void;
}

/** First non-empty representative row per distinct value of `key` (the combobox options are de-duped people). */
const dedupeBy = (list: ResponsesData[], key: 'full_name' | 'email'): ResponsesData[] => {
	const seen = new Set<string>();
	const out: ResponsesData[] = [];
	list.forEach((row) => {
		const value = row[key];
		if (value && !seen.has(value)) {
			seen.add(value);
			out.push(row);
		}
	});
	return out;
};

/**
 * Manual approval (no QR): pick a guest by NAME and EMAIL (two searchable comboboxes — email is the unique key,
 * so a shared name is disambiguated by the email combobox), then check in their tickets one tap at a time. The
 * table drops the name (the comboboxes already show it) and shows seat (seated events only, first), the
 * ticket-type tag, the scanned-status tag (same colours as the Attendee List), and an icon check-in button —
 * unscanned tickets sort to the top. A successful check-in lifts a concise verdict to the right panel and
 * invalidates the shared scan caches so the attendance donuts + Scanned-Tickets/Analytics update too. A scanned
 * ticket also offers "Undo check-in" (after a confirm), which clears its check-in for this show and refreshes
 * the same caches.
 */
export const ManualApprovalPanel = ({ eventId, onResult }: ManualApprovalPanelProps) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { resolveByName } = useTicketTypeColors();

	const [selectedName, setSelectedName] = useState<string | null>(null);
	const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
	const [undoTarget, setUndoTarget] = useState<ResponsesData | null>(null);
	const canUndo = canUndoCheckIn(localStorage.getItem('accessToken'));

	const {
		data: attendees,
		isFetching,
		isError,
		refetch,
	} = useQuery({
		queryKey: ['scanner-attendees', eventId],
		queryFn: () => getEventAttendees(eventId),
		enabled: !!eventId,
	});

	const rows = useMemo(() => attendees ?? [], [attendees]);
	const isInitialLoad = isFetching && rows.length === 0;
	// The list is one un-paginated page; if it filled the cap, some guests aren't searchable here yet.
	const isTruncated = rows.length >= ATTENDEE_PAGE_SIZE;

	const nameOptions = useMemo(
		() =>
			dedupeBy(rows, 'full_name')
				.map((row) => ({ value: row.full_name, label: row.full_name }))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[rows]
	);

	// Every ticket and order number a guest holds, so typing either one finds them.
	const numbersByEmail = useMemo(() => {
		const numbers = new Map<string, Set<string>>();
		rows.forEach((row) => {
			if (!row.email) return;
			const set = numbers.get(row.email) ?? new Set<string>();
			if (row.ticket_id) set.add(`#${row.ticket_id}`);
			if (row.order_id) set.add(`#${row.order_id}`);
			numbers.set(row.email, set);
		});
		return numbers;
	}, [rows]);

	// The email combobox narrows to the picked name's people, so a name collision forces an explicit email pick.
	const emailOptions = useMemo(() => {
		const pool = selectedName ? rows.filter((row) => row.full_name === selectedName) : rows;
		return dedupeBy(pool, 'email')
			.map((row) => ({
				value: row.email,
				label: row.email,
				description: row.full_name,
				keywords: Array.from(numbersByEmail.get(row.email) ?? []).join(' '),
			}))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [rows, selectedName, numbersByEmail]);

	// One row per ticket for the chosen attendee (email = unique key); unscanned first.
	const guestTickets = useMemo(() => {
		if (!selectedEmail) return [];
		return rows
			.filter((row) => row.email === selectedEmail && row.ticket_id)
			.sort((a, b) => Number(a.is_validated) - Number(b.is_validated));
	}, [rows, selectedEmail]);

	const hasSeats = useMemo(() => guestTickets.some((row) => row.seat_id), [guestTickets]);
	const unscannedIds = useMemo(
		() => guestTickets.filter((row) => !row.is_validated).map((row) => row.ticket_id),
		[guestTickets]
	);

	const ticketLabel = (row: ResponsesData) =>
		row.seat_id
			? `${row.ticket_types_string} · ${t('scanner-manual-seat', 'Seat')} ${row.seat_id}`
			: row.ticket_types_string;

	const refreshScans = () => {
		refetch();
		queryClient.invalidateQueries({ queryKey: ['scan-history', eventId] });
		queryClient.invalidateQueries({ queryKey: ['event-scans', eventId] });
	};

	const {
		mutate: checkIn,
		isLoading: isCheckingIn,
		variables: pending,
	} = useMutation(validateTicketsManually, {
		onSuccess: (_data, vars) => {
			const checked = rows.filter((row) => vars.ticketIds.includes(row.ticket_id));
			onResult({
				type: 'success',
				guestName: selectedName ?? checked[0]?.full_name ?? '',
				email: selectedEmail ?? checked[0]?.email ?? '',
				tickets: checked.map(ticketLabel),
			});
			// Refresh this guest's scanned status AND the shared scan caches a check-in changes (the attendance
			// donuts on this page + the Scanned-Tickets/Analytics aggregates).
			refreshScans();
		},
		onError: (error: unknown) => {
			airbrake.notify({ error: 'Error checking in ticket manually', context: { eventId } });
			onResult({
				type: 'error',
				message: extractValidationError(error) || t('scanner-validation-failed', 'Validation failed'),
			});
		},
	});

	const { mutate: undo, isLoading: isUndoing } = useMutation(undoCheckIn, {
		onSuccess: (_data, vars) => {
			const undone = rows.filter((row) => row.ticket_id === vars.ticketId);
			setUndoTarget(null);
			onResult({
				type: 'undone',
				guestName: selectedName ?? undone[0]?.full_name ?? '',
				email: selectedEmail ?? undone[0]?.email ?? '',
				tickets: undone.map(ticketLabel),
			});
			refreshScans();
		},
		onError: (error: unknown) => {
			setUndoTarget(null);
			airbrake.notify({ error: 'Error undoing a check-in', context: { eventId } });
			onResult({
				type: 'error',
				message: extractValidationError(error) || t('scanner-undo-failed', "Couldn't undo the check-in"),
			});
		},
	});

	const isPending = (ticketIds: number[]) =>
		isCheckingIn && !!pending && ticketIds.every((id) => pending.ticketIds.includes(id));

	const handleNameChange = (value: string | number) => {
		const name = String(value);
		setSelectedName(name);
		// Auto-select the email when the name maps to exactly one; otherwise force an explicit email pick.
		const emails = dedupeBy(
			rows.filter((row) => row.full_name === name),
			'email'
		);
		setSelectedEmail(emails.length === 1 ? emails[0].email : null);
	};

	const handleEmailChange = (value: string | number) => {
		const email = String(value);
		setSelectedEmail(email);
		const match = rows.find((row) => row.email === email);
		if (match?.full_name) setSelectedName(match.full_name);
	};

	// Seat (seated) gets the most room (labels like "Sec A · Row 5 · Seat 12" are long); the action column is a
	// tight fixed track for the single icon button.
	const gridColumns = hasSeats
		? 'minmax(96px, 2fr) minmax(80px, 1.4fr) minmax(64px, 1fr) 88px'
		: 'minmax(110px, 1.6fr) minmax(80px, 1fr) 88px';

	// A failed look-up load must read as a failure with a retry — not as "No guests found" — since manual
	// approval is the fallback when the camera is down.
	if (isError && rows.length === 0) {
		return (
			<PunchupErrorState
				title={t('scanner-manual-load-error-title', "Couldn't load the guest list")}
				description={t('scanner-manual-load-error-desc', 'Something went wrong fetching attendees for this show.')}
				onRetry={() => refetch()}
				retryLabel={t('button-try-again', 'Try again')}
			/>
		);
	}

	return (
		<div className={styles.panel}>
			<div className={styles.lookup}>
				<PunchupCombobox
					options={nameOptions}
					value={selectedName}
					onChange={handleNameChange}
					placeholder={t('scanner-manual-name', 'Guest name')}
					searchPlaceholder={t('scanner-manual-name-search', 'Search by name')}
					isLoading={isInitialLoad}
					emptyLabel={t('scanner-manual-no-guests', 'No guests found')}
				/>
				<PunchupCombobox
					options={emailOptions}
					value={selectedEmail}
					onChange={handleEmailChange}
					placeholder={t('scanner-manual-email', 'Guest email')}
					searchPlaceholder={t('scanner-manual-email-search', 'Search by email, ticket # or order #')}
					isLoading={isInitialLoad}
					emptyLabel={t('scanner-manual-no-guests', 'No guests found')}
				/>
			</div>

			{isTruncated && (
				<p className={styles.prompt}>
					{t(
						'scanner-manual-truncated',
						'Showing the first {{count}} guests — if a guest is missing, find them on the Attendee List.',
						{
							count: ATTENDEE_PAGE_SIZE,
						}
					)}
				</p>
			)}

			{!selectedEmail ? (
				<p className={styles.prompt}>
					{t('scanner-manual-prompt', 'Pick a guest by name and email to see their tickets.')}
				</p>
			) : (
				<>
					<PunchupTableContainer columns={gridColumns}>
						<thead>
							<PunchupHeaderRow>
								{hasSeats && <PunchupColumnHeader>{t('scanner-manual-col-seat', 'Seat')}</PunchupColumnHeader>}
								<PunchupColumnHeader>{t('scanner-manual-col-type', 'Ticket type')}</PunchupColumnHeader>
								<PunchupColumnHeader>{t('scanner-manual-col-status', 'Status')}</PunchupColumnHeader>
								<PunchupColumnHeader>{''}</PunchupColumnHeader>
							</PunchupHeaderRow>
						</thead>
						<tbody>
							{guestTickets.length === 0 ? (
								<PunchupTableEmptyRow text={t('scanner-manual-no-tickets', 'No tickets for this guest.')} />
							) : (
								guestTickets.map((row) => {
									const checkingThis = isPending([row.ticket_id]);
									const checkInLabel = row.is_validated
										? t('scanner-manual-done', 'Checked in')
										: t('scanner-manual-checkin', 'Check in');
									return (
										<PunchupDataRow key={row.ticket_id}>
											{hasSeats && <td>{row.seat_id || '—'}</td>}
											<td>
												<PunchupTicketTypeTag
													name={row.ticket_types_string}
													color={resolveByName(row.ticket_types_string)}
												/>
											</td>
											<td>
												<PunchupTag
													size="sm"
													label={
														row.is_validated ? t('scanner-scanned', 'Scanned') : t('scanner-not-scanned', 'Not scanned')
													}
													background={row.is_validated ? SCANNED_BG : NOT_SCANNED_BG}
												/>
											</td>
											<td className={styles.actionCell}>
												{row.is_validated && canUndo && (
													<button
														type="button"
														className={styles.checkInButton}
														disabled={isUndoing}
														onClick={() => setUndoTarget(row)}
														aria-label={t('scanner-undo-checkin', 'Undo check-in')}
														title={t('scanner-undo-checkin', 'Undo check-in')}
													>
														<RotateCcw size={16} aria-hidden />
													</button>
												)}
												<button
													type="button"
													className={row.is_validated ? styles.checkedInButton : styles.checkInButton}
													disabled={row.is_validated || checkingThis}
													onClick={() => checkIn({ ticketIds: [row.ticket_id], eventID: Number(eventId) })}
													aria-label={checkInLabel}
													title={checkInLabel}
												>
													{checkingThis ? (
														<PunchupSpinner size={16} />
													) : row.is_validated ? (
														<Check size={16} aria-hidden />
													) : (
														<UserCheck size={16} aria-hidden />
													)}
												</button>
											</td>
										</PunchupDataRow>
									);
								})
							)}
						</tbody>
					</PunchupTableContainer>

					{unscannedIds.length > 1 && (
						<div className={styles.allRow}>
							<PunchupButton
								variant="outline"
								isLoading={isPending(unscannedIds)}
								onClick={() => checkIn({ ticketIds: unscannedIds, eventID: Number(eventId) })}
							>
								{t('scanner-manual-checkin-all', 'Check in all ({{count}})', { count: unscannedIds.length })}
							</PunchupButton>
						</div>
					)}
				</>
			)}

			{undoTarget &&
				createPortal(
					<PunchupConfirmDialog
						icon={<RotateCcw size={24} />}
						iconVariant="destructive"
						title={t('scanner-undo-confirm-title', 'Undo this check-in?')}
						text={t(
							'scanner-undo-confirm-text',
							'{{ticket}} will show as not scanned for this show and can be scanned again.',
							{ ticket: ticketLabel(undoTarget) }
						)}
						isLoading={isUndoing}
						buttons={[
							{
								label: t('button-cancel', 'Cancel'),
								variant: 'secondary',
								onClick: () => setUndoTarget(null),
							},
							{
								label: t('scanner-undo-checkin', 'Undo check-in'),
								variant: 'destructive',
								icon: <RotateCcw size={16} />,
								onClick: () => undo({ ticketId: undoTarget.ticket_id, eventID: Number(eventId) }),
							},
						]}
					/>,
					document.body
				)}
		</div>
	);
};
